import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronDownIcon, ChevronRightIcon, CoinsIcon, ReceiptTextIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchInput } from "@/components/ui/composed/search-input";
import { Empty, EmptyDescription } from "@/components/ui/empty";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useDebounce } from "@/hooks/use-debounce";
import { formatDate } from "@/lib/format";
import * as m from "@/paraglide/messages";
import { $adminListBillingUsers } from "@/server/actions/billing";

import { CreditAdjustDialog } from "./-components/credit-adjust-dialog";
import { LedgerPanel } from "./-components/ledger-panel";

export const Route = createFileRoute("/admin/credit")({
	component: RouteComponent,
});

type BillingUserItem = {
	id: string;
	name: string;
	email: string;
	createdAt: Date;
	balance: number;
	latestBillingAt: Date | null;
};

function RouteComponent() {
	const [searchInput, setSearchInput] = useState("");
	const debouncedSearch = useDebounce(searchInput, 300);

	const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
		queryKey: [
			"billing",
			"admin",
			"users",
			{ pageSize: 20, search: debouncedSearch || undefined },
		],
		queryFn: async ({ pageParam }) =>
			$adminListBillingUsers({
				data: {
					page: pageParam,
					pageSize: 20,
					search: debouncedSearch || undefined,
				},
			}),
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			const totalPages = Math.ceil(lastPage.total / lastPage.pageSize);
			return lastPage.page < totalPages ? lastPage.page + 1 : undefined;
		},
	});

	const items = data?.pages.flatMap((page) => page.data) ?? [];

	return (
		<div className="container mx-auto px-2 py-6">
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<ReceiptTextIcon className="size-5" />
						{m.adminNav_credit()}
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<SearchInput
						wrapperClassName="max-w-sm"
						placeholder={m.adminCredit_searchPlaceholder()}
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
					/>

					{isLoading ? (
						<div className="py-8 text-center text-muted-foreground">
							{m.common_loading()}
						</div>
					) : items.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-10" />
									<TableHead>{m.adminCredit_colUser()}</TableHead>
									<TableHead className="text-right">
										{m.adminCredit_colBalance()}
									</TableHead>
									<TableHead>{m.adminCredit_colLatestBilling()}</TableHead>
									<TableHead>{m.adminCredit_colCreatedAt()}</TableHead>
									<TableHead className="w-[120px]">
										{m.adminCredit_colActions()}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{items.map((user) => (
									<BillingRow key={user.id} user={user} />
								))}
							</TableBody>
						</Table>
					) : (
						<Empty>
							<EmptyDescription>
								{debouncedSearch ? m.adminCredit_noMatch() : m.adminCredit_empty()}
							</EmptyDescription>
						</Empty>
					)}

					{hasNextPage && (
						<div className="flex justify-center pt-4">
							<Button
								variant="outline"
								onClick={() => fetchNextPage()}
								disabled={isFetchingNextPage}
							>
								{isFetchingNextPage ? m.common_loading() : m.common_loadMore()}
							</Button>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

function BillingRow({ user }: { user: BillingUserItem }) {
	const [expanded, setExpanded] = useState(false);
	const [creditDialogOpen, setCreditDialogOpen] = useState(false);

	return (
		<>
			<TableRow className="cursor-pointer" onClick={() => setExpanded((value) => !value)}>
				<TableCell className="w-10 px-2">
					{expanded ? (
						<ChevronDownIcon className="size-4 text-muted-foreground" />
					) : (
						<ChevronRightIcon className="size-4 text-muted-foreground" />
					)}
				</TableCell>
				<TableCell>
					<div>
						<p className="text-sm font-medium">{user.name ?? "—"}</p>
						<p className="text-xs text-muted-foreground">{user.email ?? "—"}</p>
					</div>
				</TableCell>
				<TableCell className="text-right text-sm font-medium tabular-nums">
					{user.balance.toLocaleString()}
				</TableCell>
				<TableCell className="text-sm text-muted-foreground">
					{formatDate(user.latestBillingAt)}
				</TableCell>
				<TableCell className="text-sm text-muted-foreground">
					{formatDate(user.createdAt)}
				</TableCell>
				<TableCell>
					<Button
						variant="ghost"
						size="sm"
						className="gap-2"
						onClick={(e) => {
							e.stopPropagation();
							setCreditDialogOpen(true);
						}}
					>
						<CoinsIcon className="size-4" />
						{m.adminSubs_manageCredits()}
					</Button>
				</TableCell>
			</TableRow>

			{expanded && (
				<TableRow>
					<TableCell colSpan={6} className="bg-muted/30 p-0">
						<LedgerPanel userId={user.id} />
					</TableCell>
				</TableRow>
			)}

			<CreditAdjustDialog
				open={creditDialogOpen}
				onOpenChange={setCreditDialogOpen}
				userId={user.id}
				userName={user.name}
			/>
		</>
	);
}
